import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuthContext } from '../hooks/useAuthContext';
import { useLogout } from '../hooks/useLogout';
import { getUserSubmission, updateUserProfile } from '../services/firebase';
import { getFirestore, doc, getDoc } from 'firebase/firestore';
import Container from '../components/common/Container';
import Button from '../components/common/Button';
import { Heading } from '../components/common/TextComponents';
// import IntroductionSection from '../components/features/dashboard/profile/IntroductionSection';
// import ResumeUploadForm from '../components/features/dashboard/ResumeUploadForm';

import ProfileCompletionTracker from '../components/features/dashboard/profile/ProfileCompletionTracker';
import GeneralInformationSection from '../components/features/dashboard/profile/GeneralInformationSection';
import ProfilePictureSection from '../components/features/dashboard/profile/ProfilePictureSection';
import JobPreferencesSection from '../components/features/dashboard/profile/JobPreferencesSection';
import OptionalEnhancementsSection from '../components/features/dashboard/profile/OptionalEnhancementsSection';
import PublicProfileLink from '../components/features/profile/PublicProfileLink';
import EmailNotificationsToggle from '../components/features/profile/EmailNotificationsToggle';
// import EmailVerificationStatus from '../components/features/profile/EmailVerificationStatus';
import ExternalLinkPreferences from '../components/features/profile/ExternalLinkPreferences';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { useDropzone } from 'react-dropzone';
import { getStorage, ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { updateProfile } from 'firebase/auth';
import { toast } from 'react-hot-toast';
import { useProfileCompletion } from '../hooks/useProfileCompletion';

const Profile = () => {
  const { user, userProfile, fetchUserProfile } = useAuthContext();
  const { logout } = useLogout();
  const navigate = useNavigate();
  const { completionPercentage, missingFields, fieldStatuses } = useProfileCompletion();
  
  // Core state
  const [submissionData, setSubmissionData] = useState(null);
  const [submissionId, setSubmissionId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  
  // Edit states for basic information
  const [editingName, setEditingName] = useState(false);
  const [editingLinkedIn, setEditingLinkedIn] = useState(false);
  const [editingWebsite, setEditingWebsite] = useState(false);
  const [editingProfessionalInfo, setEditingProfessionalInfo] = useState(false);
  const [editingLocation, setEditingLocation] = useState(false);
  const [editingJobSearchStatus, setEditingJobSearchStatus] = useState(false);
  const [editingProfilePicture, setEditingProfilePicture] = useState(false);
  
  // Profile picture upload state
  const [isUploadingPicture, setIsUploadingPicture] = useState(false);
  const [uploadError, setUploadError] = useState('');
  
  // Basic profile data
  const [profileData, setProfileData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    linkedin: '',
    website: '',
    profilePicture: '',
    currentRole: '',
    companyName: '',
    companyId: '',
    location: '',
    locationData: null,
    jobSearchStatus: ''
  });
  
  // Temporary form values for editing
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [linkedin, setLinkedin] = useState('');
  const [website, setWebsite] = useState('');
  const [currentRole, setCurrentRole] = useState('');
  const [companyName, setCompanyName] = useState('');
  const [companyId, setCompanyId] = useState('');
  const [location, setLocation] = useState('');
  const [locationData, setLocationData] = useState(null);
  const [jobSearchStatus, setJobSearchStatus] = useState('');
  
  // Location search state
  const [locationSuggestions, setLocationSuggestions] = useState([]);
  const [isLocationLoading, setIsLocationLoading] = useState(false);
  const [showLocationSuggestions, setShowLocationSuggestions] = useState(false);
  const locationDebounceTimeout = useRef(null);
  const locationCacheRef = useRef(new Map());
  const [locationSessionToken, setLocationSessionToken] = useState(null);
  
  // Career preferences state (preserved for data compatibility but not displayed)
  const [preferences, setPreferences] = useState({});
  
  // Saving states
  const [isSaving, setIsSaving] = useState(false);
  
  // Profile completion states
  const [profileCompletion, setProfileCompletion] = useState({
    percentage: 0,
    missingItems: []
  });

  // Note: Purple outline system shows incomplete sections without auto-scrolling

  // Handle logout
  const handleLogout = async () => {
    // Navigate to landing page first to avoid race conditions with protected routes
    navigate('/');
    // Then perform the logout
    await logout();
  };

  // Calculate profile completion - focuses only on essential networking information
  const calculateProfileCompletion = (userData, preferences, submissionData) => {
    // Essential fields for networking - excludes career preferences, resume, and introduction
    const essentialCriteria = [
      {
        id: 'firstName',
        isComplete: (userData?.basicInfo?.firstName || userData?.userInfo?.firstName)?.trim()
      },
      {
        id: 'lastName', 
        isComplete: (userData?.basicInfo?.lastName || userData?.userInfo?.lastName)?.trim()
      },
      {
        id: 'currentRole',
        isComplete: userData?.basicInfo?.currentRole?.trim()
      },
      {
        id: 'companyName',
        isComplete: userData?.basicInfo?.companyName?.trim()
      },
      {
        id: 'location',
        isComplete: userData?.basicInfo?.location?.trim()
      },
      {
        id: 'linkedin',
        isComplete: (userData?.basicInfo?.linkedinUrl || userData?.profileLinks?.linkedin)?.trim()
      }
    ];

    const completedCount = essentialCriteria.filter(criterion => criterion.isComplete).length;
    const percentage = Math.round((completedCount / essentialCriteria.length) * 100);
    const missingItems = essentialCriteria
      .filter(criterion => !criterion.isComplete)
      .map(criterion => criterion.id);

    return { percentage, missingItems };
  };
  
  useEffect(() => {
    if (!user && !loading) {
      navigate('/');
      return;
    }
    
    // Check if user is on waitlist
    if (user && userProfile && userProfile.status === 'waitlist') {
      navigate('/waitlist');
      return;
    }
    
    const fetchUserData = async () => {
      if (user) {
        setLoading(true);
        try {
          // Fetch user submission data
          try {
            const submissionResult = await getUserSubmission(user.uid);
            
            if (submissionResult.success) {
              setSubmissionData(submissionResult.data);
              
              if (submissionResult.submissionId) {
                setSubmissionId(submissionResult.submissionId);
              }
              
              // Set preferences from submission data
              if (submissionResult.data?.preferences) {
                setPreferences(submissionResult.data.preferences);
              }
            } else {
              console.warn(`Could not load user submission: ${submissionResult.error}`);
            }
          } catch (err) {
            console.warn(`Error fetching user submission: ${err.message}`);
          }
          
          // Fetch user profile for basic information
          try {
            const db = getFirestore();
            const userProfileRef = doc(db, 'user_profiles', user.uid);
            const userProfileSnap = await getDoc(userProfileRef);
            
            if (userProfileSnap.exists()) {
              const userData = userProfileSnap.data();
              
              // Look for onboarding data in basicInfo and preferences (NEW)
              const basicInfo = userData.basicInfo || {};
              const userInfo = userData.userInfo || {}; // Fallback to old structure
              const profileLinks = userData.profileLinks || {};
              
              // Set basic profile data - handle missing fields gracefully
              const profile = {
                // Use actual full name if available, otherwise empty strings for editing
                firstName: basicInfo.firstName || userInfo.firstName || '',
                lastName: basicInfo.lastName || userInfo.lastName || '',
                email: basicInfo.email || userInfo.email || user?.email || '',
                linkedin: basicInfo.linkedinUrl || profileLinks.linkedin || '',
                website: profileLinks.website || '',
                // Profile picture - prioritize custom uploaded photo, then Google photo
                profilePicture: userData.profilePicture || basicInfo.photoURL || userData.photoURL || user?.photoURL || '',
                currentRole: basicInfo.currentRole || '',
                companyName: basicInfo.companyName || '',
                companyId: basicInfo.companyId || '',
                location: basicInfo.location || '',
                locationData: basicInfo.locationData || null,
                jobSearchStatus: (userData.preferences && userData.preferences.jobSearchStatus) || ''
              };
              
              setProfileData(profile);
              setFirstName(profile.firstName);
              setLastName(profile.lastName);
              setLinkedin(profile.linkedin);
              setWebsite(profile.website);
              setCurrentRole(profile.currentRole);
              setCompanyName(profile.companyName);
              setCompanyId(basicInfo.companyId || '');
              setLocation(profile.location);
              setLocationData(basicInfo.locationData || null);
              setJobSearchStatus(profile.jobSearchStatus);
              
              // Set preferences from user profile if not already set from submission
              if (!preferences || Object.keys(preferences).length === 0) {
                if (userData.preferences) {
                  setPreferences(userData.preferences);
                }
              }

              // Calculate profile completion after all data is loaded
              const finalPreferences = preferences && Object.keys(preferences).length > 0 
                ? preferences 
                : userData.preferences;
              
              const completion = calculateProfileCompletion(userData, finalPreferences, submissionData);
              setProfileCompletion(completion);
            }
          } catch (err) {
            console.warn(`Error fetching user profile: ${err.message}`);
          }
        } catch (err) {
          console.error('Error fetching user data:', err);
          setError('Could not load your profile data. Please try again later.');
        } finally {
          setLoading(false);
        }
      }
    };
    
    fetchUserData();
  }, [user, navigate]);

  // Debug logging for profile completion
  useEffect(() => {
    if (!loading && userProfile) {
      console.log('\n🔍 ===== PROFILE COMPLETION DEBUG =====');
      console.log(`📊 Completion: ${completionPercentage}%`);
      console.log(`❌ Missing Fields (${missingFields.length}):`, missingFields);
      
      if (fieldStatuses && fieldStatuses.length > 0) {
        console.log('\n📋 Field Status Breakdown:');
        fieldStatuses.forEach(field => {
          const status = field.isComplete ? '✅' : '❌';
          console.log(`${status} ${field.label} (${field.id})`);
        });
      }
      
      console.log('\n🔍 User Profile Data:');
      console.log('- First Name:', userProfile.basicInfo?.firstName || '(empty)');
      console.log('- Last Name:', userProfile.basicInfo?.lastName || '(empty)');
      console.log('- Current Role:', userProfile.basicInfo?.currentRole || '(empty)');
      console.log('- Company:', userProfile.basicInfo?.companyName || '(empty)');
      console.log('- Location:', userProfile.basicInfo?.location || '(empty)');
      console.log('- LinkedIn:', userProfile.basicInfo?.linkedinUrl || '(empty)');
      
      console.log('\n📸 Profile Picture Paths:');
      console.log('- userProfile.profilePicture:', userProfile.profilePicture || '(empty)');
      console.log('- userProfile.basicInfo.photoURL:', userProfile.basicInfo?.photoURL === null ? 'null' : (userProfile.basicInfo?.photoURL || '(empty)'));
      console.log('- userProfile.photoURL:', userProfile.photoURL || '(empty)');
      console.log('- user.photoURL (from Auth):', user?.photoURL || '(empty)');
      
      console.log('\n🔍 Full basicInfo object:');
      console.log(JSON.stringify(userProfile.basicInfo, null, 2));
      
      console.log('\n🔍 Top-level profile fields:');
      console.log('- profilePicture:', userProfile.profilePicture || '(not set)');
      console.log('- photoURL:', userProfile.photoURL || '(not set)');
      console.log('=====================================\n');
    }
  }, [loading, userProfile, completionPercentage, missingFields, fieldStatuses]);

  // Helper function to format URLs
  const formatUrl = (url) => {
    if (!url) return '';
    
    // Add https:// prefix if missing
    if (!/^https?:\/\//i.test(url)) {
      return `https://${url}`;
    }
    
    return url;
  };

  // Start editing a field
  const startEditing = (field) => {
    if (field === 'name') {
      setEditingName(true);
    } else if (field === 'linkedin') {
      setEditingLinkedIn(true);
    } else if (field === 'website') {
      setEditingWebsite(true);
    } else if (field === 'professionalInfo') {
      setEditingProfessionalInfo(true);
    } else if (field === 'location') {
      setEditingLocation(true);
    } else if (field === 'jobSearchStatus') {
      setEditingJobSearchStatus(true);
    } else if (field === 'profilePicture') {
      setEditingProfilePicture(true);
    }
  };
  
  // Profile picture upload function - now handled by ProfilePictureSection component

  const uploadProfilePicture = async (file) => {
    setIsUploadingPicture(true);
    setUploadError('');
    
    try {
      const storage = getStorage();
      const filename = `users/${user.uid}/profile_pictures/${Date.now()}_${file.name}`;
      const storageRef = ref(storage, filename);
      
      // Upload file
      await uploadBytes(storageRef, file);
      
      // Get download URL
      const downloadURL = await getDownloadURL(storageRef);
      
      // Update profile data in Firestore
      const updateData = {
        profilePicture: downloadURL,
        'basicInfo.photoURL': downloadURL // Also update basicInfo for consistency
      };
      
      const result = await updateUserProfile(user.uid, updateData);
      
      if (result.success) {
        // Update Firebase Auth user profile
        await updateProfile(user, {
          photoURL: downloadURL
        });
        
        // Refresh user profile in AuthContext to update sidebar immediately
        if (fetchUserProfile) {
          await fetchUserProfile(user);
        }
        
        // Update local state
        setProfileData(prev => ({
          ...prev,
          profilePicture: downloadURL
        }));
        
        setEditingProfilePicture(false);
        toast.success('Profile picture updated successfully!');
      } else {
        throw new Error(result.error || 'Failed to update profile');
      }
    } catch (error) {
      console.error('Error uploading profile picture:', error);
      setUploadError('Failed to upload image. Please try again.');
      toast.error('Failed to upload image');
    } finally {
      setIsUploadingPicture(false);
    }
  };

  const removeProfilePicture = async () => {
    try {
      setIsUploadingPicture(true);
      
      // Update profile to remove custom picture (will fall back to Google photo if available)
      const updateData = {
        profilePicture: null
      };
      
      const result = await updateUserProfile(user.uid, updateData);
      
      if (result.success) {
        // Update Firebase Auth user profile to remove custom photo
        await updateProfile(user, {
          photoURL: null
        });
        
        // Refresh user profile in AuthContext to update sidebar immediately
        if (fetchUserProfile) {
          await fetchUserProfile(user);
        }
        
        // Update local state
        setProfileData(prev => ({
          ...prev,
          profilePicture: null
        }));
        
        setEditingProfilePicture(false);
        toast.success('Profile picture removed successfully!');
      } else {
        throw new Error(result.error || 'Failed to remove profile picture');
      }
    } catch (error) {
      console.error('Error removing profile picture:', error);
      toast.error('Failed to remove profile picture');
    } finally {
      setIsUploadingPicture(false);
    }
  };

  // Cancel editing and reset to original values
  const cancelEditing = (field) => {
    if (field === 'name') {
      setFirstName(profileData.firstName);
      setLastName(profileData.lastName);
      setEditingName(false);
    } else if (field === 'linkedin') {
      setLinkedin(profileData.linkedin);
      setEditingLinkedIn(false);
    } else if (field === 'website') {
      setWebsite(profileData.website);
      setEditingWebsite(false);
    } else if (field === 'professionalInfo') {
      setCurrentRole(profileData.currentRole);
      setCompanyName(profileData.companyName);
      setCompanyId(profileData.companyId || '');
      setEditingProfessionalInfo(false);
    } else if (field === 'location') {
      setLocation(profileData.location);
      setLocationData(profileData.locationData || null);
      setEditingLocation(false);
    } else if (field === 'jobSearchStatus') {
      setJobSearchStatus(profileData.jobSearchStatus);
      setEditingJobSearchStatus(false);
    } else if (field === 'profilePicture') {
      setEditingProfilePicture(false);
      setUploadError('');
    }
  };
  
  // Save changes for a specific field - updated to handle GeneralInformationSection field names
  const saveField = async (field, value, locationData = null) => {
    if (!user?.uid) return;
    
    try {
      setIsSaving(true);
      setError('');
      setSuccessMessage('');
      
      let updateData = {};
      
      // Handle individual field updates from GeneralInformationSection
      if (field === 'firstName') {
        updateData = {
          'basicInfo.firstName': value.trim()
        };
      } else if (field === 'lastName') {
        updateData = {
          'basicInfo.lastName': value.trim()
        };
      } else if (field === 'currentRole') {
        updateData = {
          'basicInfo.currentRole': value.trim()
        };
      } else if (field === 'companyName') {
        updateData = {
          'basicInfo.companyName': value.trim()
        };
      } else if (field === 'location') {
        updateData = {
          'basicInfo.location': value.trim(),
          'basicInfo.locationData': locationData || null
        };

      } else if (field === 'linkedin') {
        updateData = {
          'basicInfo.linkedinUrl': value.trim()
        };
      } else if (field === 'website') {
        updateData = {
          'profileLinks.website': value.trim()
        };
      }
      // Legacy field handlers for compatibility
      else if (field === 'name') {
        updateData = {
          'basicInfo.firstName': firstName.trim(),
          'basicInfo.lastName': lastName.trim()
        };
      } else if (field === 'professionalInfo') {
        updateData = {
          'basicInfo.currentRole': currentRole.trim(),
          'basicInfo.companyName': companyName.trim(),
          'basicInfo.companyId': companyId || null
        };
      } else if (field === 'jobSearchStatus') {
        updateData = {
          'preferences.jobSearchStatus': jobSearchStatus
        };
      }
      
      const result = await updateUserProfile(user.uid, updateData);
      
      if (result.success) {
        // Update local state with new values
        if (field === 'firstName') {
          setProfileData(prev => ({
            ...prev,
            firstName: value.trim()
          }));
        } else if (field === 'lastName') {
          setProfileData(prev => ({
            ...prev,
            lastName: value.trim()
          }));
        } else if (field === 'currentRole') {
          setProfileData(prev => ({
            ...prev,
            currentRole: value.trim()
          }));
        } else if (field === 'companyName') {
          setProfileData(prev => ({
            ...prev,
            companyName: value.trim()
          }));
        } else if (field === 'location') {
          setProfileData(prev => ({
            ...prev,
            location: value.trim(),
            locationData: locationData || null
          }));

        } else if (field === 'linkedin') {
          setProfileData(prev => ({
            ...prev,
            linkedin: value.trim()
          }));
        } else if (field === 'website') {
          setProfileData(prev => ({
            ...prev,
            website: value.trim()
          }));
        }
        // Legacy field handlers for compatibility
        else if (field === 'name') {
          setProfileData(prev => ({
            ...prev,
            firstName: firstName.trim(),
            lastName: lastName.trim()
          }));
          setEditingName(false);
        } else if (field === 'professionalInfo') {
          setProfileData(prev => ({
            ...prev,
            currentRole: currentRole.trim(),
            companyName: companyName.trim(),
            companyId: companyId
          }));
          setEditingProfessionalInfo(false);
        } else if (field === 'jobSearchStatus') {
          setProfileData(prev => ({
            ...prev,
            jobSearchStatus: jobSearchStatus
          }));
          setEditingJobSearchStatus(false);
        }
        
        setSuccessMessage('Changes saved successfully!');
        
        // Recalculate profile completion after successful save
        const db = getFirestore();
        const userProfileRef = doc(db, 'user_profiles', user.uid);
        const userProfileSnap = await getDoc(userProfileRef);
        
        if (userProfileSnap.exists()) {
          const userData = userProfileSnap.data();
          const completion = calculateProfileCompletion(userData, preferences, submissionData);
          setProfileCompletion(completion);
        }
        
        // Clear success message after 3 seconds
        setTimeout(() => {
          setSuccessMessage('');
        }, 3000);
      } else {
        throw new Error(result.error || 'Failed to save changes');
      }
    } catch (err) {
      console.error('Error saving profile:', err);
      setError('Could not save your changes. Please try again later.');
      
      // Clear error message after 5 seconds
      setTimeout(() => {
        setError('');
      }, 5000);
    } finally {
      setIsSaving(false);
    }
  };

  // Location search functions
  const generateSessionToken = () => {
    return Date.now().toString(36) + Math.random().toString(36).substr(2);
  };

  const fetchLocationSuggestions = async (input) => {
    console.log('🔍 Profile.js: fetchLocationSuggestions called', {
      input,
      inputLength: input?.length,
      trimmedLength: input?.trim()?.length,
      sessionToken: locationSessionToken,
      timestamp: new Date().toISOString()
    });

    if (!input.trim() || input.length < 2) {
      console.log('📝 Profile.js: Input too short, clearing suggestions', {
        input: input.trim(),
        length: input.length
      });
      setLocationSuggestions([]);
      setShowLocationSuggestions(false);
      return;
    }

    const trimmedInput = input.trim().toLowerCase();
    console.log('📝 Profile.js: Proceeding with search', { trimmedInput });
    
    // Check cache first
    if (locationCacheRef.current.has(trimmedInput)) {
      const cachedResults = locationCacheRef.current.get(trimmedInput);
      console.log('💾 Profile.js: Using cached results', {
        trimmedInput,
        cachedResultsCount: cachedResults.length
      });
      setLocationSuggestions(cachedResults);
      setShowLocationSuggestions(cachedResults.length > 0);
      return;
    }

    console.log('🌐 Profile.js: Making API call to placesAutocomplete', {
      input: input.trim(),
      sessionToken: locationSessionToken
    });

    setIsLocationLoading(true);

    try {
      const functions = getFunctions();
      const placesAutocomplete = httpsCallable(functions, 'placesAutocomplete');
      
      const result = await placesAutocomplete({
        input: input.trim(),
        sessionToken: locationSessionToken
      });

      console.log('📡 Profile.js: placesAutocomplete response received', {
        hasData: !!result.data,
        success: result.data?.success,
        suggestionsCount: result.data?.suggestions?.length || 0,
        fullResult: result
      });

      if (result.data.success) {
        const suggestions = result.data.suggestions;
        
        console.log('✅ Profile.js: Successfully received suggestions', {
          count: suggestions.length,
          suggestions: suggestions.map(s => ({ placeId: s.placeId, description: s.description }))
        });
        
        // Cache the results
        locationCacheRef.current.set(trimmedInput, suggestions);
        
        // Clear old cache entries if cache gets too large
        if (locationCacheRef.current.size > 50) {
          const entries = Array.from(locationCacheRef.current.entries());
          locationCacheRef.current.clear();
          entries.slice(-25).forEach(([key, value]) => {
            locationCacheRef.current.set(key, value);
          });
          console.log('🧹 Profile.js: Cache cleaned up');
        }
        
        setLocationSuggestions(suggestions);
        setShowLocationSuggestions(true);
      } else {
        console.log('❌ Profile.js: API call unsuccessful', {
          result: result.data
        });
        setLocationSuggestions([]);
        setShowLocationSuggestions(false);
        locationCacheRef.current.set(trimmedInput, []);
      }
    } catch (error) {
      console.error('💥 Profile.js: Error fetching location suggestions:', {
        errorMessage: error.message,
        errorCode: error.code,
        errorStack: error.stack,
        input: input.trim(),
        sessionToken: locationSessionToken
      });
      setLocationSuggestions([]);
      setShowLocationSuggestions(false);
    } finally {
      setIsLocationLoading(false);
      console.log('🏁 Profile.js: Search completed');
    }
  };

  const handleLocationSelect = async (suggestion) => {
    console.log('📍 Profile.js: handleLocationSelect called', {
      suggestion,
      placeId: suggestion.placeId,
      description: suggestion.description,
      sessionToken: locationSessionToken,
      timestamp: new Date().toISOString()
    });

    setLocation(suggestion.description);
    setShowLocationSuggestions(false);
    setIsLocationLoading(true);

    try {
      console.log('🌐 Profile.js: Making API call to placeDetails', {
        placeId: suggestion.placeId,
        sessionToken: locationSessionToken
      });

      const functions = getFunctions();
      const placeDetails = httpsCallable(functions, 'placeDetails');
      
      const result = await placeDetails({
        placeId: suggestion.placeId,
        sessionToken: locationSessionToken
      });

      console.log('📡 Profile.js: placeDetails response received', {
        hasData: !!result.data,
        success: result.data?.success,
        location: result.data?.location,
        fullResult: result
      });

      if (result.data.success) {
        console.log('✅ Profile.js: Successfully received place details', {
          formattedAddress: result.data.location.formattedAddress,
          city: result.data.location.components?.city,
          state: result.data.location.components?.state,
          country: result.data.location.components?.country
        });

        setLocationData(result.data.location);
        setLocationSessionToken(generateSessionToken());
        console.log('🔄 Profile.js: Generated new session token');
      } else {
        console.log('❌ Profile.js: placeDetails API call unsuccessful', {
          result: result.data
        });
      }
    } catch (error) {
      console.error('💥 Profile.js: Error fetching place details:', {
        errorMessage: error.message,
        errorCode: error.code,
        errorStack: error.stack,
        placeId: suggestion.placeId,
        sessionToken: locationSessionToken
      });
      setLocationData(null);
    } finally {
      setIsLocationLoading(false);
      console.log('🏁 Profile.js: Location selection completed');
    }
  };

  const handleCompanySelect = (company) => {
    if (company) {
      setCompanyId(company.id);
      setCompanyName(company.name);
    }
  };

  // Initialize location session token
  React.useEffect(() => {
    setLocationSessionToken(generateSessionToken());
  }, []);

  // Handle clicks outside to close location suggestions
  React.useEffect(() => {
    const handleClickOutside = (event) => {
      if (showLocationSuggestions && !event.target.closest('.location-search-container')) {
        setShowLocationSuggestions(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showLocationSuggestions]);

  // Cleanup debounce timeout on unmount
  React.useEffect(() => {
    return () => {
      if (locationDebounceTimeout.current) {
        clearTimeout(locationDebounceTimeout.current);
      }
    };
  }, []);



  if (!user) {
    return (
      <div className="min-h-screen bg-gray-50 flex flex-col justify-center py-12 sm:px-6 lg:px-8">
        <Container variant="default">
          <div className="text-center">
            <div className="flex justify-center mb-6">
              <svg className="h-12" viewBox="0 0 138 30" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M25.067 12.161C25.067 13.172 24.5 14.075 23.659 14.6C24.5 15.124 25.067 16.027 25.067 17.04V18.617C25.067 20.378 23.637 21.806 21.881 21.806H14.987V7.398H21.881C23.637 7.398 25.067 8.825 25.067 10.588V12.161ZM21.881 16.456C21.881 15.576 21.168 14.863 20.286 14.863H18.173V17.972H20.286C21.168 17.972 21.881 17.335 21.881 16.456ZM21.881 12.736C21.881 11.855 21.168 11.143 20.286 11.143H18.173V14.252H20.286C21.168 14.252 21.881 13.615 21.881 12.736ZM31.061 21.817H27.943V7.395H31.061V21.817ZM36.112 7.398H46.221V11.143H39.298V14.025H45.454V16.995H39.298V17.989H46.221V21.806H36.112V7.398ZM59.227 21.923C58.046 21.923 56.951 21.605 56.017 21.044L54.829 22.231L52.516 19.915L53.704 18.729C53.143 17.793 52.823 16.701 52.823 15.517C52.823 14.334 53.143 13.242 53.704 12.306L52.516 11.12L54.829 8.804L56.017 9.991C56.951 9.43 58.046 9.112 59.227 9.112C62.642 9.112 65.397 11.868 65.397 15.281C65.397 18.695 62.642 21.923 59.227 21.923ZM59.227 12.858C57.782 12.858 56.598 14.04 56.598 15.487C56.598 16.934 57.782 18.114 59.227 18.114C60.672 18.114 61.856 16.934 61.856 15.487C61.856 14.04 60.672 12.858 59.227 12.858ZM76.142 7.458V18.304C76.142 19.597 75.09 20.647 73.8 20.647H71.459C70.168 20.647 69.117 19.597 69.117 18.304V7.458H66V18.304C66 21.348 68.417 23.766 71.459 23.766H73.8C76.843 23.766 79.26 21.348 79.26 18.304V7.458H76.142ZM82.856 21.817H86.005V7.395H82.856V21.817ZM93.636 11.231H89.831V7.458H97.429C98.72 7.458 99.772 8.507 99.772 9.772V17.608C99.772 19.478 98.309 21.008 96.502 21.201L94.935 21.367V19.648C91.952 19.648 93.636 21.817 93.636 21.817H90.518V13.812C90.518 12.374 91.785 11.231 93.636 11.231ZM96.624 17.573V11.231H93.753V17.573H96.624ZM101.756 21.817H105.085V7.395H101.756V21.817ZM109.928 7.398H120.037V11.143H113.114V14.025H119.27V16.995H113.114V17.989H120.037V21.806H109.928V7.398ZM129.045 7.398H132.164V17.632H137.448V21.806H129.045V7.398Z" fill="#3a0ca3"/>
              </svg>
            </div>
            <h1 className="text-4xl font-bold text-gray-900 sm:text-5xl">Please log in</h1>
            <p className="mt-4 text-lg text-gray-600">You need to be logged in to view this page.</p>
            <Button as="a" href="/" className="mt-8">Return to Homepage</Button>
          </div>
        </Container>
      </div>
    );
  }

  return (
    <div className="relative min-h-screen">
      {/* Background gradient */}
      <div className="absolute inset-0 bg-gradient-to-b from-amber-100 via-pink-300 to-purple-500 opacity-10 -z-10"></div>
      
      {/* Main Content */}
      <Container variant="default">
          <div className="mt-4 profile-mobile-content">
            <div className="mx-auto max-w-4xl">
              {loading ? (
                <div className="py-12 flex justify-center">
                  <div className="inline-block h-12 w-12 animate-spin rounded-full border-4 border-solid border-purple-500 border-r-transparent"></div>
                </div>
              ) : (
                <>
                  {/* Header */}
                  <div className="mb-8">
                    {/* Mobile layout: Title and description stacked, button below */}
                    <div className="sm:hidden">
                      <div className="mb-4">
                        <Heading className="text-gray-900 dark:text-gray-100">Profile</Heading>
                        <p className="mt-2 text-gray-600 dark:text-gray-300">
                          Manage your account information, career preferences, and professional profile.
                        </p>
                      </div>
                      <div className="flex justify-center pb-4">
                        <PublicProfileLink 
                          userId={user?.uid} 
                          className="profile-mobile-button"
                        />
                      </div>
                    </div>
                    
                    {/* Desktop layout: Original design */}
                    <div className="hidden sm:flex sm:items-start sm:justify-between gap-4">
                      <div className="flex-1">
                        <Heading className="text-gray-900 dark:text-gray-100">Profile</Heading>
                        <p className="mt-2 text-gray-600 dark:text-gray-300">
                          Manage your account information, career preferences, and professional profile.
                        </p>
                      </div>
                      <div className="flex-shrink-0">
                        <PublicProfileLink userId={user?.uid} />
                      </div>
                    </div>
                  </div>

                  {/* Profile Completion Tracker - Only shows when not complete */}
                  <ProfileCompletionTracker 
                    completionPercentage={completionPercentage}
                    missingItems={missingFields}
                  />

                  {/* Profile Picture Section */}
                  <ProfilePictureSection
                    profilePicture={profileData.profilePicture}
                    onUpload={uploadProfilePicture}
                    onRemove={removeProfilePicture}
                    isUploading={isUploadingPicture}
                    uploadError={uploadError}
                    userName={`${profileData.firstName} ${profileData.lastName}`.trim()}
                  />



                  {/* Success/Error Messages */}
                  {error && (
                    <div className="mb-6 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-md dark:bg-red-900/20 dark:border-red-800 dark:text-red-300">
                      {error}
                    </div>
                  )}
                  
                  {successMessage && (
                    <div className="mb-6 bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-md dark:bg-green-900/20 dark:border-green-800 dark:text-green-300">
                      {successMessage}
                    </div>
                  )}

                  {/* General Information Section - Replaces Basic Info, Professional Links, and Professional Info */}
                  <GeneralInformationSection
                    profileData={profileData}
                    onSave={saveField}
                    isLoading={isSaving}
                  />

                  {/* Job Preferences Section - Private to member/admins */}
                  <JobPreferencesSection
                    jobSearchStatus={profileData.jobSearchStatus}
                    onSave={saveField}
                    isLoading={isSaving}
                  />

                  {/* Resume & Introduction Section - Consolidated - TEMPORARILY HIDDEN */}
                  {/* <IntroductionSection
                    profileSummaryData={profileSummaryData}
                    user={user}
                    profileCompletion={profileCompletion}
                    preferences={preferences}
                    submissionData={submissionData}
                    onProfileCompletionUpdate={(completion) => setProfileCompletion(completion)}
                    calculateProfileCompletion={calculateProfileCompletion}
                  /> */}

                  {/* Email Verification Status - Hidden per user request */}
                  {/* <EmailVerificationStatus /> */}

                  {/* Email Notifications Toggle */}
                  <EmailNotificationsToggle />

                  {/* External Link Preferences */}
                  <ExternalLinkPreferences />

                  {/* Logout Button */}
                  <div className="mt-12 flex justify-center">
                    <button
                      onClick={handleLogout}
                      className="px-6 py-2 text-sm font-medium text-gray-500 hover:text-red-600 hover:bg-red-50 rounded-md transition-colors dark:text-gray-400 dark:hover:text-red-400 dark:hover:bg-red-900/20"
                    >
                      Log Out
                    </button>
                  </div>
                </>
              )}
            </div>
            {/* Extra padding at the bottom to ensure floating button doesn't overlap content */}
            <div className="h-20"></div>
          </div>
        </Container>
    </div>
  );
};

export default Profile;
